import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faEye, faEyeSlash } from '@/icons';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { SignupProgress, USER_SIGNUP_STEPS } from '../components/SignupProgress';
import { authApiService } from '../services/authApiService';
import { config } from '../config/environment';
import { debugError } from '../utils/debug';

export const RegisterPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [status, setStatus] = useState<'form' | 'submitting' | 'done' | 'err'>('form');
    const [message, setMessage] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    useEffect(() => {
        document.title = 'Create password | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!token) return <Navigate to="/auth/login" replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            {content}
        </div>
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);
        if (password.length < 8) {
            setPasswordError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setPasswordError('Passwords do not match.');
            return;
        }
        if (config.turnstileSiteKey && !turnstileToken) {
            setPasswordError('Please complete the verification challenge.');
            return;
        }
        setStatus('submitting');
        try {
            const result = await authApiService.signupRegisterPromote(token, password, turnstileToken);
            navigate('/auth/register/done', { replace: true, state: { plan: result.data?.plan } });
        } catch (err: unknown) {
            debugError('signupRegisterPromote', err);
            setStatus('err');
            setMessage(err instanceof Error ? err.message : 'Could not complete registration. Please try again.');
        }
    };

    if (status === 'done') {
        return wrapper(
            <div style={themedStyles.card.default}>
                <SignupProgress currentStep={3} steps={USER_SIGNUP_STEPS} />

                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                </div>
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: '#f0fdf4',
                        color: '#16a34a',
                        fontSize: '28px',
                        marginBottom: theme.spacing.md,
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.sm }}>You're all set!</h1>
                    <p style={{ ...themedStyles.text.body, marginTop: 0 }}>Your account is ready. Sign in to get started.</p>
                </div>
                <Link
                    to="/auth/login"
                    style={{ ...themedStyles.button.primary, display: 'block', textAlign: 'center', textDecoration: 'none' }}
                >
                    Sign In
                </Link>
            </div>
        );
    }

    if (status === 'err') {
        return wrapper(
            <div style={themedStyles.card.default}>
                <p style={themedStyles.text.body}>{message}</p>
                <Link to="/auth/login" style={{ color: theme.colors.primary }}>Try again</Link>
            </div>
        );
    }

    return wrapper(
        <div style={themedStyles.card.default}>
            <SignupProgress currentStep={2} steps={USER_SIGNUP_STEPS} />

            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '64px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Create your password</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>Choose a password to complete your account setup.</p>
            </div>

            <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                    <label htmlFor="reg-pw" style={themedStyles.text.label}>Password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            id="reg-pw"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoComplete="new-password"
                            disabled={status === 'submitting'}
                            placeholder="At least 8 characters"
                            style={{ ...themedStyles.input.default, paddingRight: '40px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(v => !v)}
                            tabIndex={-1}
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.text.secondary }}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                    <label htmlFor="reg-pw-confirm" style={themedStyles.text.label}>Confirm password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            id="reg-pw-confirm"
                            name="confirm-password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            disabled={status === 'submitting'}
                            placeholder="Re-enter your password"
                            style={{ ...themedStyles.input.default, paddingRight: '40px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirmPassword(v => !v)}
                            tabIndex={-1}
                            style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.text.secondary }}
                            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                            <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                        </button>
                    </div>
                </div>

                {passwordError && (
                    <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.status.error }}>{passwordError}</span>
                )}

                {!!config.turnstileSiteKey && (
                    <TurnstileWidget siteKey={config.turnstileSiteKey} onTokenChange={setTurnstileToken} />
                )}

                <button
                    type="submit"
                    disabled={!password || !confirmPassword || status === 'submitting' || (!!config.turnstileSiteKey && !turnstileToken)}
                    style={{
                        ...themedStyles.button.primary,
                        ...(!password || !confirmPassword || status === 'submitting' || (!!config.turnstileSiteKey && !turnstileToken) ? themedStyles.button.disabled : {}),
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    }}
                >
                    {status === 'submitting' ? 'Creating your account…' : 'Create Password'}
                </button>
            </form>
        </div>
    );
};
